import React, { useState } from 'react'
import {
  FormControl,
  FormLabel,
  styled,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'capitalize',
  flex: 1,
  padding: 4,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  '&.Mui-selected': {
    backgroundColor: theme.palette.primarySoft.main,
    color: theme.palette.primarySoft.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primarySoft.main
    }
  }
}))

/**
 * SwitchButtonsProps interface defines the properties for the SwitchButtons component.
 */
export interface SwitchButtonsProps {
  /** The title for the switch buttons group */
  title?: string
  /** Control prop for managing the selected state */
  control?: [any, React.Dispatch<React.SetStateAction<any>>]
  /** The list of buttons to display, each with a value and label */
  buttonList: Array<{
    value: number | boolean
    label: string
  }>
}

/**
 * SwitchButtons component
 * This component renders a group of toggle buttons that can be used to switch between different options.
 * @param {SwitchButtonsProps} props - The properties for the SwitchButtons component.
 * @returns {JSX.Element} The rendered SwitchButtons component.
 * @example
 * <SwitchButtons
 *   title="Select Option"
 *   control={[selectedOption, setSelectedOption]}
 *   buttonList={[
 *    { value: 1, label: 'Option 1' },
 *    { value: 2, label: 'Option 2' },
 *    { value: 3, label: 'Option 3' }
 *   ]}
 * />
 */
const SwitchButtons: React.FC<SwitchButtonsProps> = ({
  title,
  control,
  buttonList
}) => {
  const [innerValue, setInnerValue] = useState<number | null>(null)
  const [selected, setSelected] = control ?? [innerValue, setInnerValue]

  const handleChange = (_: any, newValue: number) => {
    if (newValue !== null) {
      setSelected(newValue)
    }
  }

  return (
    <FormControl component='fieldset' sx={{ width: '100%' }}>
      <FormLabel component='legend'>{title}</FormLabel>
      <ToggleButtonGroup
        value={selected}
        exclusive
        onChange={handleChange}
        aria-label='switch options'
        sx={{ width: '200px', marginTop: 1 }}
      >
        {buttonList.map(({ value, label }, index) => (
          <StyledToggleButton
            key={`switch-btn-${label}-${index}`}
            value={value}
          >
            {label}
          </StyledToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormControl>
  )
}

export default SwitchButtons
